import { useSearchParams, Link } from "react-router-dom";
import { AppConfig } from "../../utils/config";
import styles from "./OrderSuccess.module.css";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { OrderConfirmationResponse } from "../../types/order/order";

export default function OrderSuccess() {
  const [searchParams] = useSearchParams();
  const checkoutSessionId = searchParams.get("session_id");
  const [order, setOrder] = useState<OrderConfirmationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!checkoutSessionId) {
      setLoading(false);
      setError("No session ID provided");
      return;
    }

    const fetchOrder = async () => {
      try {
        const response = await fetch(`${AppConfig.apiUrl}/confirm`, {
          method: "POST",
          body: JSON.stringify({ sessionId: checkoutSessionId }),
        });

        if (!response.ok) {
          throw new Error("Failed to fetch order details");
        }

        const data: OrderConfirmationResponse = await response.json();
        setOrder(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [checkoutSessionId]);

  const formatPrice = (price: number) => {
    return `£${price.toFixed(2)}`;
  };

  const formatAddress = (order: OrderConfirmationResponse) => {
    const parts = [
      order.deliveryName,
      order.deliveryLine1,
      order.deliveryLine2,
      order.deliveryTown,
      order.deliveryCounty,
      order.deliveryPostCode,
      order.deliveryCountry,
    ].filter(Boolean);
    return parts;
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.loadingSpinner}></div>
          <p>Loading your order details...</p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <h2 className={styles.errorTitle}>Unable to load order</h2>
          <p className={styles.errorText}>
            {error || "Order details not found"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Order Received | Martin Townsend Postal History</title>
        <meta
          name="description"
          content="Your order has been received. Thank you for purchasing from Martin Townsend's postal history collection."
        />
      </Helmet>
      <div className={styles.container}>
        <div className={styles.successBanner}>
          <div className={styles.checkIcon}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={3}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h1 className={styles.title}>Order Received!</h1>
          <p className={styles.subtitle}>
            Thank you for your purchase. A confirmation email will be sent
            shortly.
          </p>
        </div>

        <div className={styles.orderDetails}>
          <div className={styles.section}>
            <h2 className={styles.sectionHeader}>Items Ordered</h2>
            <div className={styles.sectionContent}>
              <ul className={styles.itemsList}>
                {order.items.map((item) => (
                  <li key={item.id} className={styles.orderItem}>
                    <img
                      src={`${AppConfig.smImgUrl}/${item.id}.jpg`}
                      alt={`Item ${item.reference}`}
                      className={styles.itemImage}
                    />
                    <div className={styles.itemInfo}>
                      <div
                        className={styles.itemTitle}
                        dangerouslySetInnerHTML={{ __html: item.title }}
                      />
                      <span className={styles.itemReference}>
                        Ref: {item.reference}
                      </span>
                    </div>
                    <span className={styles.itemPrice}>
                      {formatPrice(item.price)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className={styles.section}>
            <h2 className={styles.sectionHeader}>Delivery Address</h2>
            <div className={styles.sectionContent}>
              <div className={styles.address}>
                {formatAddress(order).map((line, index) => (
                  <p key={index} className={styles.addressLine}>
                    {line}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.summarySection}>
          <div className={styles.summaryRow}>
            <span>
              Subtotal ({order.items.length} item
              {order.items.length !== 1 ? "s" : ""})
            </span>
            <span>{formatPrice(order.totalCost - order.postageCost)}</span>
          </div>
          <div className={styles.summaryRow}>
            <span>Postage</span>
            <span>
              {order.postageCost === 0
                ? "Free"
                : formatPrice(order.postageCost)}
            </span>
          </div>
          <div className={`${styles.summaryRow} ${styles.total}`}>
            <span>Total</span>
            <span>{formatPrice(order.totalCost)}</span>
          </div>
        </div>

        <div className={styles.footer}>
          <p className={styles.footerText}>
            If you have any questions about your order, please don't hesitate to
            contact us.
          </p>
          <Link to="/" className={styles.continueButton}>
            Continue Shopping
          </Link>
        </div>
      </div>
    </>
  );
}
